import "./style.css";
import { WheelManager } from "./wheel";

// Color palettes for different themes
const THEME_COLORS: Record<string, string[]> = {
  indigo: ["#312e81", "#3730a3", "#4338ca", "#4f46e5", "#6366f1", "#818cf8"],
  emerald: ["#064e3b", "#065f46", "#047857", "#059669", "#10b981", "#34d399"],
  rose: ["#881337", "#9f1239", "#be123c", "#e11d48", "#f43f5e", "#fb7185"],
  amber: ["#78350f", "#92400e", "#b45309", "#d97706", "#f59e0b", "#fbbf24"],
  cyan: ["#164e63", "#155e75", "#0891b2", "#06b6d4", "#22d3ee", "#67e8f9"],
};

interface SpinnerProfile {
  opts: string[];
  theme: string;
  speed: number;
}

document.addEventListener("DOMContentLoaded", () => {
  // 1. Core State
  const wheel = new WheelManager(["Pizza", "Tacos", "Sushi", "Salad", "Burgers"]);
  let activeTheme = "indigo";

  let currentAngle = 0;
  let currentSpeed = 0;
  let maxSpinSpeed = 720; // degrees per second
  let spinState: "idle" | "spinning" | "decelerating" = "idle";

  // Deceleration state
  let decelerationStartAngle = 0;
  let decelerationTargetAngle = 0;
  let decelerationStartTime = 0;
  let decelerationDuration = 0;

  // DOM Elements
  const wheelSvg = document.getElementById("wheel-svg") as SVGSVGElement | null;
  const spinBtn = document.getElementById("spin-btn") as HTMLButtonElement | null;

  // Option Input Elements
  const addOptionForm = document.getElementById("add-option-form") as HTMLFormElement | null;
  const optionInput = document.getElementById("option-input") as HTMLInputElement | null;
  const optionsListContainer = document.getElementById(
    "options-list-container",
  ) as HTMLDivElement | null;
  const optionsCountEl = document.getElementById("options-count") as HTMLSpanElement | null;
  const clearAllBtn = document.getElementById("clear-all-btn") as HTMLButtonElement | null;

  // Customization Elements
  const speedSlider = document.getElementById("speed-slider") as HTMLInputElement | null;
  const speedValueEl = document.getElementById("speed-value") as HTMLSpanElement | null;
  const colorPresetBtns = document.querySelectorAll(".color-preset-btn");

  // Saved Profiles Elements
  const saveProfileForm = document.getElementById("save-profile-form") as HTMLFormElement | null;
  const profileNameInput = document.getElementById("profile-name-input") as HTMLInputElement | null;
  const profileSelect = document.getElementById("profile-select") as HTMLSelectElement | null;
  const deleteProfileBtn = document.getElementById(
    "delete-profile-btn",
  ) as HTMLButtonElement | null;

  // Overlay Elements
  const resultOverlay = document.getElementById("result-overlay") as HTMLDivElement | null;
  const resultTextEl = document.getElementById("result-text") as HTMLDivElement | null;
  const closeResultBtn = document.getElementById("close-result-btn") as HTMLButtonElement | null;
  const emptyStateText = document.getElementById("empty-state-text") as HTMLDivElement | null;

  // 2. State & URL Synchronization Logic

  /**
   * Serializes active wheel settings and updates the URL hash
   */
  const saveStateToUrl = () => {
    try {
      const stateObj: SpinnerProfile = {
        opts: wheel.options,
        theme: activeTheme,
        speed: maxSpinSpeed,
      };
      const jsonStr = JSON.stringify(stateObj);
      // UTF-8 friendly Base64 encoding
      const encoded = btoa(
        encodeURIComponent(jsonStr).replace(/%([0-9A-F]{2})/g, (_, p1) => {
          return String.fromCharCode(parseInt(p1, 16));
        }),
      );
      history.replaceState(null, "", `#state=${encoded}`);
    } catch (err) {
      console.error("Failed to save state to URL:", err);
    }
  };

  /**
   * Pulls state from URL hash, decodes and applies it
   */
  const loadStateFromUrl = (): boolean => {
    try {
      const hash = window.location.hash;
      const match = hash.match(/state=([^&]+)/);
      if (!match) return false;

      const encoded = match[1];
      const decodedJson = decodeURIComponent(
        atob(encoded)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join(""),
      );

      const stateObj = JSON.parse(decodedJson) as SpinnerProfile;
      if (stateObj && Array.isArray(stateObj.opts)) {
        // Load options
        while (wheel.options.length > 0) {
          wheel.removeOption(0);
        }
        stateObj.opts.forEach((opt) => wheel.addOption(opt));

        // Load active theme
        if (stateObj.theme && THEME_COLORS[stateObj.theme]) {
          activeTheme = stateObj.theme;
          updateThemeUI();
        }

        // Load speed settings
        if (typeof stateObj.speed === "number") {
          maxSpinSpeed = stateObj.speed;
          if (speedSlider) speedSlider.value = maxSpinSpeed.toString();
          if (speedValueEl) speedValueEl.textContent = `${maxSpinSpeed}°/s`;
        }

        renderWheel();
        renderList();
        return true;
      }
    } catch (err) {
      console.error("Failed to load state from URL:", err);
    }
    return false;
  };

  // 3. LocalStorage Profile Persistence

  /**
   * Retrieve all saved profiles from LocalStorage
   */
  const getSavedProfiles = (): Record<string, SpinnerProfile> => {
    try {
      const stored = localStorage.getItem("spinner_profiles");
      if (stored) {
        return JSON.parse(stored) as Record<string, SpinnerProfile>;
      }
    } catch (err) {
      console.error("Failed to fetch saved profiles:", err);
    }
    return {};
  };

  /**
   * Save current configuration as a named profile
   */
  const saveProfile = (name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return;

    const profiles = getSavedProfiles();
    profiles[trimmed] = {
      opts: [...wheel.options],
      theme: activeTheme,
      speed: maxSpinSpeed,
    };

    try {
      localStorage.setItem("spinner_profiles", JSON.stringify(profiles));
      renderProfilesSelect();
      if (profileSelect) {
        profileSelect.value = trimmed;
        if (deleteProfileBtn) deleteProfileBtn.disabled = false;
      }
    } catch (err) {
      console.error("Failed to save profile:", err);
    }
  };

  /**
   * Delete a named profile from LocalStorage
   */
  const deleteProfile = (name: string) => {
    const profiles = getSavedProfiles();
    if (profiles[name]) {
      delete profiles[name];
      try {
        localStorage.setItem("spinner_profiles", JSON.stringify(profiles));
        renderProfilesSelect();
      } catch (err) {
        console.error("Failed to delete profile:", err);
      }
    }
  };

  /**
   * Redraw the saved profiles select options
   */
  const renderProfilesSelect = () => {
    if (!profileSelect || !deleteProfileBtn) return;

    const currentSelection = profileSelect.value;
    profileSelect.innerHTML = `<option value="" disabled selected>Select profile to load...</option>`;

    const profiles = getSavedProfiles();
    const names = Object.keys(profiles).sort();

    names.forEach((name) => {
      const opt = document.createElement("option");
      opt.value = name;
      opt.textContent = name;
      profileSelect.appendChild(opt);
    });

    if (currentSelection && profiles[currentSelection]) {
      profileSelect.value = currentSelection;
      deleteProfileBtn.disabled = false;
    } else {
      profileSelect.value = "";
      deleteProfileBtn.disabled = true;
    }
  };

  // 4. Render Utilities

  /**
   * Helper to truncate long labels
   */
  const truncateText = (text: string, maxLength: number = 12): string => {
    return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
  };

  /**
   * Sync active theme color configuration visually to elements and variables
   */
  const updateThemeUI = () => {
    colorPresetBtns.forEach((b) => {
      const theme = b.getAttribute("data-theme");
      if (theme === activeTheme) {
        b.classList.add("active");
        b.setAttribute("aria-checked", "true");
      } else {
        b.classList.remove("active");
        b.setAttribute("aria-checked", "false");
      }
    });

    const colors = THEME_COLORS[activeTheme] || THEME_COLORS.indigo;
    document.documentElement.style.setProperty("--color-primary", colors[3]);
    document.documentElement.style.setProperty("--spinner-color", colors[3]);
  };

  /**
   * Redraw the SVG Wheel segments and labels
   */
  const renderWheel = () => {
    if (!wheelSvg) return;
    wheelSvg.innerHTML = "";

    const N = wheel.options.length;

    // Draw empty/placeholder state if not enough choices
    if (N === 0) {
      const circle = document.createElementNS("http://www.w3.org/2000/svg", "circle");
      circle.setAttribute("cx", "150");
      circle.setAttribute("cy", "150");
      circle.setAttribute("r", "130");
      circle.setAttribute(
        "class",
        "fill-zinc-900/40 stroke-zinc-800 stroke-2 [stroke-dasharray:6,6]",
      );
      wheelSvg.appendChild(circle);

      const text = document.createElementNS("http://www.w3.org/2000/svg", "text");
      text.setAttribute("x", "150");
      text.setAttribute("y", "150");
      text.setAttribute("text-anchor", "middle");
      text.setAttribute("dominant-baseline", "middle");
      text.setAttribute("class", "fill-zinc-600 font-medium text-xs font-sans");
      text.textContent = "Add choices to preview wheel";
      wheelSvg.appendChild(text);
      return;
    }

    const segmentAngle = 360 / N;
    const colors = THEME_COLORS[activeTheme] || THEME_COLORS.indigo;

    for (let i = 0; i < N; i++) {
      const startAngle = i * segmentAngle;
      const endAngle = (i + 1) * segmentAngle;

      const fillIndex = i % colors.length;
      const fillColor = colors[fillIndex];

      if (N === 1) {
        // Simple case: Draw a full circle
        const circle = document.createElementNS("http://www.w3.org/2000/svg", "circle");
        circle.setAttribute("cx", "150");
        circle.setAttribute("cy", "150");
        circle.setAttribute("r", "130");
        circle.setAttribute("fill", fillColor);
        circle.setAttribute("class", "stroke-zinc-950 stroke-1");
        wheelSvg.appendChild(circle);

        // Center text
        const textG = document.createElementNS("http://www.w3.org/2000/svg", "g");
        textG.setAttribute("transform", "translate(150, 150)");

        const text = document.createElementNS("http://www.w3.org/2000/svg", "text");
        text.setAttribute("text-anchor", "middle");
        text.setAttribute("dominant-baseline", "middle");
        text.setAttribute("fill", "#f4f4f5");
        text.setAttribute("class", "font-display text-sm font-semibold tracking-wide");
        text.textContent = truncateText(wheel.options[i]);

        textG.appendChild(text);
        wheelSvg.appendChild(textG);
        continue;
      }

      // Convert degree coordinates to radians for trigonometry
      const startRad = (startAngle * Math.PI) / 180;
      const endRad = (endAngle * Math.PI) / 180;

      const r = 130;
      const cx = 150;
      const cy = 150;

      const x1 = cx + r * Math.cos(startRad);
      const y1 = cy + r * Math.sin(startRad);
      const x2 = cx + r * Math.cos(endRad);
      const y2 = cy + r * Math.sin(endRad);

      const largeArcFlag = segmentAngle > 180 ? 1 : 0;

      // Draw slice segment path
      const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
      const d = `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;
      path.setAttribute("d", d);
      path.setAttribute("fill", fillColor);
      path.setAttribute("class", "stroke-zinc-950/80 stroke-1 transition-colors duration-300");
      wheelSvg.appendChild(path);

      // Add radial text label
      const midAngle = startAngle + segmentAngle / 2;
      const isLeftHalf = midAngle > 90 && midAngle < 270;
      const textDist = r * 0.65;

      const textG = document.createElementNS("http://www.w3.org/2000/svg", "g");
      // Translate to center, rotate, move outward, and flip if on left half for readability
      const transform = `translate(${cx}, ${cy}) rotate(${midAngle}) translate(${textDist}, 0) rotate(${isLeftHalf ? 180 : 0})`;
      textG.setAttribute("transform", transform);

      const text = document.createElementNS("http://www.w3.org/2000/svg", "text");
      text.setAttribute("text-anchor", "middle");
      text.setAttribute("dominant-baseline", "middle");
      text.setAttribute("fill", "#f4f4f5");
      text.setAttribute(
        "class",
        "font-display text-[11px] md:text-[12px] font-semibold tracking-wide select-none pointer-events-none",
      );
      text.textContent = truncateText(wheel.options[i], N > 10 ? 8 : 12);

      textG.appendChild(text);
      wheelSvg.appendChild(textG);
    }
  };

  /**
   * Update the choice list inside the settings card
   */
  const renderList = () => {
    if (!optionsListContainer || !optionsCountEl) return;

    optionsListContainer.innerHTML = "";

    const N = wheel.options.length;
    optionsCountEl.textContent = N.toString();

    if (spinBtn) {
      spinBtn.disabled = N < 2;
    }

    if (N === 0) {
      if (emptyStateText) {
        optionsListContainer.appendChild(emptyStateText);
      }
      return;
    }

    wheel.options.forEach((opt, idx) => {
      const item = document.createElement("div");
      item.className =
        "flex justify-between items-center bg-zinc-900 border border-zinc-800/80 rounded-xl px-3.5 py-2.5 text-sm transition-all duration-200 hover:border-zinc-700/50";

      const label = document.createElement("span");
      label.className = "text-zinc-200 font-medium truncate pr-2";
      label.textContent = opt;

      const removeBtn = document.createElement("button");
      removeBtn.className =
        "text-zinc-500 hover:text-rose-400 p-1 rounded-lg hover:bg-zinc-800/80 transition-all duration-200 cursor-pointer";
      removeBtn.ariaLabel = `Remove ${opt}`;
      removeBtn.innerHTML = `
        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      `;

      removeBtn.addEventListener("click", () => {
        if (spinState !== "idle") return;
        wheel.removeOption(idx);
        renderWheel();
        renderList();
        saveStateToUrl();
      });

      item.appendChild(label);
      item.appendChild(removeBtn);
      optionsListContainer.appendChild(item);
    });
  };

  // 5. Animation and Deceleration loop
  let lastTime = performance.now();

  const animationFrame = (timestamp: number) => {
    const deltaTime = (timestamp - lastTime) / 1000; // in seconds
    lastTime = timestamp;

    if (spinState === "spinning") {
      // Smoothly ramp up speed to target speed
      if (currentSpeed < maxSpinSpeed) {
        currentSpeed += maxSpinSpeed * deltaTime * 2;
        if (currentSpeed > maxSpinSpeed) {
          currentSpeed = maxSpinSpeed;
        }
      }
      currentAngle = (currentAngle + currentSpeed * deltaTime) % 360;
      updateSvgRotation(currentAngle);
      requestAnimationFrame(animationFrame);
    } else if (spinState === "decelerating") {
      const elapsed = (timestamp - decelerationStartTime) / 1000;

      if (elapsed >= decelerationDuration) {
        // Complete the stop
        currentAngle = decelerationTargetAngle % 360;
        if (currentAngle < 0) currentAngle += 360;

        currentSpeed = 0;
        spinState = "idle";
        updateSvgRotation(currentAngle);
        announceWinner();
      } else {
        // Easing calculations: Cubic ease-out
        const u = Math.min(Math.max(elapsed / decelerationDuration, 0), 1);
        const f = 1 - Math.pow(1 - u, 3);

        currentAngle =
          decelerationStartAngle + (decelerationTargetAngle - decelerationStartAngle) * f;
        updateSvgRotation(currentAngle);
        requestAnimationFrame(animationFrame);
      }
    }
  };

  const updateSvgRotation = (angle: number) => {
    if (!wheelSvg) return;
    // Rotate clockwise to match the mathematical coordinates in wheel.ts
    wheelSvg.style.transform = `rotate(${angle}deg)`;
  };

  // 6. Spin / Stop Actions Triggers

  const startSpinning = () => {
    if (wheel.options.length < 2) return;
    if (spinState !== "idle") return;

    if (resultOverlay) {
      resultOverlay.classList.remove("opacity-100");
      resultOverlay.classList.add("opacity-0", "pointer-events-none");
    }

    lastTime = performance.now();
    spinState = "spinning";
    currentSpeed = 0;

    if (spinBtn) {
      spinBtn.textContent = "Stop Wheel";
      spinBtn.className =
        "w-full py-4 px-6 font-display text-lg font-bold rounded-2xl bg-rose-500 hover:bg-rose-600 active:scale-[0.98] text-white shadow-xl shadow-rose-500/20 cursor-pointer transition-all duration-200";
    }

    toggleInputs(true);
    requestAnimationFrame(animationFrame);
  };

  const stopSpinning = () => {
    if (spinState !== "spinning") return;

    const N = wheel.options.length;
    const targetIndex = Math.floor(Math.random() * N);

    const { finalAngle, duration } = wheel.calculateStoppingAngle(
      currentAngle,
      currentSpeed,
      targetIndex,
      2.0,
    );

    decelerationStartAngle = currentAngle;
    decelerationTargetAngle = finalAngle;
    decelerationStartTime = performance.now();
    decelerationDuration = duration;
    spinState = "decelerating";

    if (spinBtn) {
      spinBtn.textContent = "Selecting...";
      spinBtn.disabled = true;
      spinBtn.className =
        "w-full py-4 px-6 font-display text-lg font-bold rounded-2xl bg-zinc-700 text-zinc-400 shadow-none cursor-not-allowed transition-all duration-200";
    }
  };

  const announceWinner = () => {
    const winnerIdx = wheel.getOptionIndexAtAngle(currentAngle);
    const winnerName = wheel.options[winnerIdx] || "No Option";

    if (resultTextEl && resultOverlay) {
      resultTextEl.textContent = winnerName;
      resultOverlay.classList.remove("opacity-0", "pointer-events-none");
      resultOverlay.classList.add("opacity-100");
    }

    if (spinBtn) {
      spinBtn.textContent = "Spin the Wheel";
      spinBtn.disabled = false;
      spinBtn.className =
        "w-full py-4 px-6 font-display text-lg font-bold rounded-2xl bg-indigo-500 hover:bg-indigo-600 active:scale-[0.98] text-white shadow-xl shadow-indigo-500/20 cursor-pointer transition-all duration-200";
    }

    toggleInputs(false);
  };

  const toggleInputs = (disable: boolean) => {
    if (optionInput) optionInput.disabled = disable;
    const addBtn = addOptionForm?.querySelector("button");
    if (addBtn) addBtn.disabled = disable;
    if (clearAllBtn) clearAllBtn.disabled = disable;

    if (profileNameInput) profileNameInput.disabled = disable;
    const saveProfileBtn = saveProfileForm?.querySelector("button");
    if (saveProfileBtn) saveProfileBtn.disabled = disable;
    if (profileSelect) profileSelect.disabled = disable;
    if (deleteProfileBtn) deleteProfileBtn.disabled = disable || !profileSelect?.value;

    const removeButtons = optionsListContainer?.querySelectorAll("button");
    removeButtons?.forEach((btn) => {
      (btn as HTMLButtonElement).disabled = disable;
    });
  };

  // 7. Bind Event Listeners

  if (addOptionForm && optionInput) {
    addOptionForm.addEventListener("submit", (e) => {
      e.preventDefault();
      const val = optionInput.value.trim();
      if (val) {
        wheel.addOption(val);
        optionInput.value = "";
        optionInput.focus();
        renderWheel();
        renderList();
        saveStateToUrl();
      }
    });
  }

  if (clearAllBtn) {
    clearAllBtn.addEventListener("click", () => {
      if (spinState !== "idle") return;

      while (wheel.options.length > 0) {
        wheel.removeOption(0);
      }
      renderWheel();
      renderList();
      saveStateToUrl();
    });
  }

  if (spinBtn) {
    spinBtn.addEventListener("click", () => {
      if (spinState === "idle") {
        startSpinning();
      } else if (spinState === "spinning") {
        stopSpinning();
      }
    });
  }

  if (closeResultBtn && resultOverlay) {
    closeResultBtn.addEventListener("click", () => {
      resultOverlay.classList.remove("opacity-100");
      resultOverlay.classList.add("opacity-0", "pointer-events-none");
    });
  }

  // Keyboard Shortcuts
  window.addEventListener("keydown", (e) => {
    if (e.key === "Enter") {
      if (document.activeElement === optionInput || document.activeElement === profileNameInput)
        return;

      if (spinState === "idle" && wheel.options.length >= 2) {
        startSpinning();
      } else if (spinState === "spinning") {
        stopSpinning();
      }
    } else if (e.key === "Escape") {
      if (spinState === "spinning") {
        stopSpinning();
      } else if (resultOverlay && !resultOverlay.classList.contains("opacity-0")) {
        resultOverlay.classList.remove("opacity-100");
        resultOverlay.classList.add("opacity-0", "pointer-events-none");
      }
    }
  });

  // Speed Slider Handler
  if (speedSlider && speedValueEl) {
    const updateMaxSpeed = () => {
      maxSpinSpeed = parseInt(speedSlider.value, 10);
      speedValueEl.textContent = `${maxSpinSpeed}°/s`;
    };
    speedSlider.addEventListener("input", updateMaxSpeed);
    speedSlider.addEventListener("change", saveStateToUrl);
    updateMaxSpeed();
  }

  // Theme Preset Click Handlers
  colorPresetBtns.forEach((btn) => {
    btn.addEventListener("click", (e) => {
      if (spinState !== "idle") return;

      const target = e.currentTarget as HTMLButtonElement;
      const theme = target.getAttribute("data-theme");
      if (theme && THEME_COLORS[theme]) {
        activeTheme = theme;
        updateThemeUI();
        renderWheel();
        saveStateToUrl();
      }
    });
  });

  // Profiles Form Submit
  if (saveProfileForm && profileNameInput) {
    saveProfileForm.addEventListener("submit", (e) => {
      e.preventDefault();
      const val = profileNameInput.value.trim();
      if (val) {
        saveProfile(val);
        profileNameInput.value = "";
      }
    });
  }

  // Profiles Load Selection
  if (profileSelect) {
    profileSelect.addEventListener("change", () => {
      const selected = profileSelect.value;
      if (!selected) return;

      const profiles = getSavedProfiles();
      const config = profiles[selected];
      if (config) {
        // Load Options
        while (wheel.options.length > 0) {
          wheel.removeOption(0);
        }
        config.opts.forEach((opt) => wheel.addOption(opt));

        // Load Theme
        activeTheme = config.theme;
        updateThemeUI();

        // Load Speed
        maxSpinSpeed = config.speed;
        if (speedSlider) speedSlider.value = maxSpinSpeed.toString();
        if (speedValueEl) speedValueEl.textContent = `${maxSpinSpeed}°/s`;

        renderWheel();
        renderList();
        saveStateToUrl();

        if (deleteProfileBtn) deleteProfileBtn.disabled = false;
      }
    });
  }

  // Profile Delete Click
  if (deleteProfileBtn && profileSelect) {
    deleteProfileBtn.addEventListener("click", () => {
      const selected = profileSelect.value;
      if (selected) {
        deleteProfile(selected);
        renderProfilesSelect();
      }
    });
  }

  // 8. Initial Load Triggers

  const hasUrlState = loadStateFromUrl();
  if (!hasUrlState) {
    saveStateToUrl();
  }
  renderProfilesSelect();
});
